import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Card,
  Typography,
  Grid2,
} from "@mui/material";
import AppButton from "../../../components/CommonReusableComponent/AppButton/AppButton";
import { Link } from "react-router-dom";
import Dropzone from "react-dropzone";
import viewIcon from "../../../assets/images/View.svg";
import { Icons } from "../../../config/MuiIcons.config";
import { AppTheme } from "../../../theme/App.theme";
import { useSnackbar } from "../../../contexts/Snackbar.context";
import { SNACKBAR_VARIANT } from "../../../config/Messages.config";
import { getFileNameFromInput } from "../../../common/CommonFunctions";

const UploadEvidenceForNCPointModal = ({
  ModalState,
  ToggleFunction,
  ActivityObj,
  HandleChangeInActivityStatusTable,
  IsViewModeForFiles,
}) => {
  const { showSnackbar } = useSnackbar();

  const { evidence } = ActivityObj;

  const [uploadedFiles, setUploadedFiles] = useState([...evidence]);

  // Function to handle file drop
  const handleDropAcceptedFiles = (acceptedFiles) => {
    const acceptedFileTypes = ["image/png", "image/jpeg", "application/pdf"];
    const acceptedFileSize = 2 * 1024 * 1024; // 2MB

    // Filter valid files based on type and size
    const validFiles = acceptedFiles.filter(
      (file) =>
        acceptedFileTypes.includes(file.type) &&
        file.size <= acceptedFileSize
    );

    // Remove duplicates based on name + size
    const nonDuplicateFiles = validFiles.filter(
      (newFile) =>
        !uploadedFiles.some(
          (existingFile) =>
            existingFile.name === newFile.name &&
            existingFile.size === newFile.size
        )
    );

    // Check for duplicate files and length limit
    if (nonDuplicateFiles.length < validFiles.length) {
      showSnackbar("Duplicate files are not allowed.", SNACKBAR_VARIANT.ERROR);
    } else if (uploadedFiles.length + validFiles.length > 3) {
      showSnackbar("You can upload up to 3 files.", SNACKBAR_VARIANT.ERROR);
    } else {
      setUploadedFiles((prev) => [...prev, ...validFiles]);
    }
  };

  // Function to handle file rejection
  const handleRejectedFiles = (fileRejections) => {
    let reason = null;
    let is_too_large = null;
    reason = fileRejections[0]?.errors[0]?.message;
    is_too_large = fileRejections[0]?.errors[0]?.code === "file-too-large";

    if (is_too_large) {
      showSnackbar("File size exceeds the limit of 2MB.", SNACKBAR_VARIANT.ERROR);
    }

    if (reason && !is_too_large) {
      showSnackbar(reason, SNACKBAR_VARIANT.ERROR);
    }
  };

  // Function to handle file delete
  const handleFileDelete = (indexToRemove) => {
    let updatedFiles = [...uploadedFiles].filter(
      (_, index) => index !== indexToRemove
    );
    setUploadedFiles(updatedFiles);
  };

  // Function to handle file view in new tab
  const viewBlobInNewTab = (fileBlob) => {
    const blobURL = URL.createObjectURL(fileBlob);
    window.open(blobURL, "_blank");
  };

  return (
    <Dialog
      open={ModalState}
      aria-labelledby="form-dialog-title"
      maxWidth="sm"
      fullWidth
      slotProps={{
        style: {
          opacity: 0.7,
        },
      }}
    >
      <DialogTitle id="responsive-dialog-title">
        <Typography
          className="inter-medium-500"
          style={{
            fontSize: "18px",
          }}
        >
          {!IsViewModeForFiles ? "Upload Evidence" : "Uploaded Files"}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={() => ToggleFunction()}
          style={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Icons.Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <div>
          <Grid2 container spacing={1}>
            {!IsViewModeForFiles && (
              <Grid2 size={12}>
                <Typography
                  className="inter-regular-400 mb-1"
                  style={{ fontSize: "15px" }}
                >
                  Select files or documents to upload
                </Typography>
                <Typography variant="caption" className="inter-regular-400">
                  Supports only .png, .jpg, .jpeg, and .pdf files. Maximum 3
                  files allowed, each up to 2MB in size.
                </Typography>
                <Dropzone
                  onDrop={handleDropAcceptedFiles}
                  onDropRejected={handleRejectedFiles}
                  maxSize={2 * 1024 * 1024} // Max 2MB
                  accept={{
                    "image/png": [".png"],
                    "image/jpeg": [".jpg", ".jpeg"],
                    "application/pdf": [".pdf"],
                  }}
                  multiple
                >
                  {({ getRootProps, getInputProps, isDragActive }) => (
                    <>
                      <div
                        style={{
                          height: "150px",
                          border: `1px solid ${AppTheme.PRIMARY_COLOR}`,
                          borderRadius: "6px",
                          marginBottom: "2px",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            display: "flex",
                            textAlign: "center",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                          {...getRootProps()}
                        >
                          <div>
                            <input {...getInputProps()} />
                            <Icons.CloudUploadIcon
                              sx={{
                                fontSize: 40,
                                color: `${AppTheme.PRIMARY_COLOR}`,
                              }}
                            />
                            {isDragActive ? (
                              <Typography
                                variant="subtitle2"
                                className="inter-regular-400 my-1"
                              >
                                Drop your file here
                              </Typography>
                            ) : (
                              <>
                                <Typography
                                  variant="subtitle2"
                                  className="inter-regular-400 my-1"
                                >
                                  Drag your file here or{" "}
                                  <Link
                                    href="#"
                                    sx={{
                                      textDecoration: "none",
                                    }}
                                  >
                                    browse
                                  </Link>
                                </Typography>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </Dropzone>
              </Grid2>
            )}

            <Grid2 size={12}>
              <Typography
                className="inter-regular-400 mb-1"
                style={{ fontSize: "15px" }}
              >
                {uploadedFiles?.length > 0 && !IsViewModeForFiles
                  ? "Uploaded Files"
                  : uploadedFiles?.length === 0 && "No files are uploaded"}
              </Typography>
            </Grid2>

            {uploadedFiles.map((file, index) => {
              const fileName = getFileNameFromInput(file);
              return (
                <Grid2
                  size={{ xs: 12, sm: 6, md: 4, lg: 4 }}
                  key={index}
                  className="my-1"
                >
                  <Card
                    variant="outlined"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "2px 4px",
                      bgcolor: "#f9f9f9",
                      borderRadius: "3px",
                      width: "100%",
                      boxShadow: "none",
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <div className="d-grid ms-2">
                        <Typography
                          variant="caption"
                          noWrap={true}
                          className="inter-regular-400"
                        >
                          {fileName}
                        </Typography>
                      </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-end gap-2">
                      {typeof file === "string" ? (
                        <Link
                          to={file}
                          target="_blank"
                          className="inter-regular-400"
                        >
                          <img
                            src={viewIcon}
                            className="cursor-pointer"
                            alt={"View File"}
                            style={{ width: "19px", height: "17px" }}
                          />
                        </Link>
                      ) : (
                        <div>
                          <img
                            src={viewIcon}
                            onClick={() => viewBlobInNewTab(file)}
                            className="cursor-pointer"
                            alt={"View File"}
                            style={{ width: "19px", height: "17px" }}
                          />
                        </div>
                      )}

                      {!IsViewModeForFiles && (
                        <div>
                          <Icons.Clear
                            className="cursor-pointer"
                            sx={{
                              fontSize: 17,
                            }}
                            onClick={() => handleFileDelete(index)}
                          />
                        </div>
                      )}
                    </div>
                  </Card>
                </Grid2>
              );
            })}
          </Grid2>

          {!IsViewModeForFiles && (
            <Grid2 container spacing={1} justifyContent={"right"}>
              <Grid2 size={{ xs: 12, sm: 3, md: 2 }} className="my-2">
                <AppButton
                  OnPress={() => {
                    HandleChangeInActivityStatusTable(
                      ActivityObj?.id,
                      "evidence",
                      uploadedFiles
                    );
                    ToggleFunction();
                    console.log("uploadedFiles", uploadedFiles);
                  }}
                >
                  Save
                </AppButton>
              </Grid2>
            </Grid2>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UploadEvidenceForNCPointModal;
